/**
 * SEO Component for GoCompareAI
 * Provides dynamic meta tags, titles, and structured data for all pages
 * Uses @dr.pogodin/react-helmet which supports React 19
 */
import { Helmet } from '@dr.pogodin/react-helmet';

const SITE_NAME = 'GoCompareAI';
const SITE_URL = 'https://www.gocompareai.com';
const DEFAULT_DESCRIPTION = 'Compare AI platforms side by side. GoCompareAI helps businesses choose the right AI using plain English, transparency, and independent comparisons.';

/**
 * SEO configurations for different page types
 */
export const SEO_CONFIG = {
  // Homepage
  home: {
    title: 'Compare AI Platforms | GoCompareAI',
    description: 'Compare AI platforms side by side. GoCompareAI helps businesses choose the right AI using plain English, transparency, and independent comparisons.',
    keywords: 'AI comparison, AI platforms, compare AI tools, AI chatbots, AI assistants, ChatGPT, Claude, Gemini',
  },
  
  // Wizard/Guide page
  guide: {
    title: 'Find Your Ideal AI Platform | GoCompareAI',
    description: 'Answer a few simple questions to discover which AI platform fits your needs. No jargon, no hype – just honest comparisons.',
    keywords: 'AI platform finder, AI recommendation, choose AI tool, AI wizard',
  },
  
  // Comparison page
  comparison: {
    title: 'Compare AI Platforms Side by Side | GoCompareAI',
    description: 'See AI platforms compared across features, pricing, data handling, and use cases. Make informed decisions with transparent information.',
    keywords: 'AI comparison, side by side AI, compare AI features, AI pricing comparison',
  },
  
  // Education/Learn page
  education: {
    title: 'Learn About AI Platforms | GoCompareAI',
    description: 'Educational resources to help you understand AI platforms, features, and best practices. Written in plain English for everyone.',
    keywords: 'AI education, learn AI, AI guide, understand AI platforms',
  },
  
  // Vendor info page
  vendors: {
    title: 'For AI Platform Vendors | GoCompareAI',
    description: 'Join GoCompareAI to showcase your AI platform. Reach users actively comparing AI tools and demonstrate your value proposition.',
    keywords: 'AI vendor portal, list AI platform, AI marketplace, vendor registration',
  },
  
  // Suggest platform page
  suggest: {
    title: 'Suggest an AI Platform | GoCompareAI',
    description: 'Know an AI platform we should include? Suggest it here. Help make GoCompareAI more comprehensive and useful.',
    keywords: 'suggest AI platform, add AI tool, AI platform submission',
  },
};

/**
 * Generate SEO-friendly title for platform pages
 */
export const generatePlatformTitle = (platform) => {
  if (!platform) return `AI Platform | ${SITE_NAME}`;
  const name = platform.name || 'AI Platform';
  return `${name} – Features, Pricing & Use Cases | ${SITE_NAME}`;
};

/**
 * Generate SEO-friendly description for platform pages
 */
export const generatePlatformDescription = (platform) => {
  if (!platform) return DEFAULT_DESCRIPTION;
  const name = platform.name || 'This AI platform';
  const summary = platform.summary || '';
  
  // Truncate to ~155 characters
  let description = `${name}: ${summary}`;
  if (description.length > 155) {
    description = description.substring(0, 152) + '...';
  }
  return description;
};

/**
 * Main SEO Component
 */
export function SEO({ 
  title,
  description,
  keywords,
  canonical,
  type = 'website',
  image,
  noindex = false,
  platform = null,
  pageType = null
}) {
  // Use page type config if provided
  let seoTitle = title;
  let seoDescription = description;
  let seoKeywords = keywords;
  
  if (pageType && SEO_CONFIG[pageType]) {
    seoTitle = seoTitle || SEO_CONFIG[pageType].title;
    seoDescription = seoDescription || SEO_CONFIG[pageType].description;
    seoKeywords = seoKeywords || SEO_CONFIG[pageType].keywords;
  }
  
  // Platform-specific SEO
  if (platform) {
    seoTitle = seoTitle || generatePlatformTitle(platform);
    seoDescription = seoDescription || generatePlatformDescription(platform);
    seoKeywords = seoKeywords || `${platform.name}, AI platform, ${(platform.useCases || []).slice(0, 3).join(', ')}`;
  }
  
  // Defaults
  seoTitle = seoTitle || SEO_CONFIG.home.title;
  seoDescription = seoDescription || DEFAULT_DESCRIPTION;
  
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href : SITE_URL);
  const imageUrl = image || `${SITE_URL}/logo-square.png`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      {seoKeywords && <meta name="keywords" content={seoKeywords} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Additional SEO signals */}
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <html lang="en" />
    </Helmet>
  );
}

/**
 * Structured Data for Platform pages (JSON-LD)
 */
export function PlatformStructuredData({ platform }) {
  if (!platform) return null;
  
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: platform.name,
    description: platform.summary || platform.description,
    applicationCategory: 'Artificial Intelligence',
    operatingSystem: 'Web',
    url: platform.links?.website,
    provider: {
      '@type': 'Organization',
      name: platform.provider || platform.name,
    },
    offers: platform.pricing?.paidPlans?.length > 0 ? {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    } : undefined,
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

/**
 * Structured Data for Organization (JSON-LD)
 * Important: Clearly defines GoCompareAI as independent from other comparison sites
 */
export function OrganizationStructuredData() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GoCompareAI',
    alternateName: 'Go Compare AI',
    url: SITE_URL,
    logo: `${SITE_URL}/logo-square.png`,
    description: 'GoCompareAI is an independent AI platform comparison service helping businesses choose the right artificial intelligence tools. We provide transparent, unbiased comparisons across 220+ AI platforms using plain English explanations. GoCompareAI is not affiliated with Go.Compare, GoCompare.com, or Future PLC.',
    foundingDate: '2024',
    slogan: 'Compare AI platforms with confidence',
    knowsAbout: [
      'Artificial Intelligence',
      'AI Platform Comparison',
      'Machine Learning Tools',
      'ChatGPT Alternatives',
      'AI Software Selection',
      'Business AI Tools'
    ],
    sameAs: [
      'https://www.instagram.com/gocompareai'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@gocompareai.com',
      contactType: 'customer service'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

/**
 * Structured Data for FAQ pages (JSON-LD)
 */
export function FAQStructuredData({ faqs }) {
  if (!faqs || faqs.length === 0) return null;
  
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

export default SEO;
